import React from 'react';
import { BeforeCloseEventArgs, DialogComponent } from '@syncfusion/ej2-react-popups';
import SystemActions from '@shared/actions/SystemActions';
import { displayApiError } from '@shared/api/apiClient';
import dataSourcesApi from '../api/dataSourcesApi';
import {
    ConnectionTypeModel,
    ConnectionValidationResult,
    DataConnectionType,
    DataSourceModel,
    DataSourceSaveModel,
    DataTypeValue,
    SourceColumn,
    SourceObject,
} from '../types';
import {
    buildSourceConfig,
    columnsFromSourceConfig,
    coerceDataTypeForProvider,
    getColumnKey,
    getDefaultConnectionConfig,
    getSourceDataType,
    getSourceObjectKey,
    hasConnectionConfigChanged,
    isFileProvider,
    mergeConnectionConfigWithDefaults,
    normalizeDataType,
    toSourceObject,
} from '../utilities';
import ConnectionDataStep from './ConnectionDataStep';
import DataSchemaStep from './DataSchemaStep';
import DataSourcePropertiesStep from './DataSourcePropertiesStep';

interface DataSourceWizardProps {
    facilityID: string;
    connectionTypes: ConnectionTypeModel[];
    source?: DataSourceModel | null;
    saving: boolean;
    onSave: (model: DataSourceSaveModel) => Promise<void>;
    onCancel: () => void;
}

type WizardErrors = Record<string, string>;
type WizardStep = 0 | 1 | 2;

const wizardSteps: { index: WizardStep; labelKey: string }[] = [
    { index: 0, labelKey: 'facility:dataSources.steps.properties' },
    { index: 1, labelKey: 'facility:dataSources.steps.connectionData' },
    { index: 2, labelKey: 'facility:dataSources.steps.dataSchema' },
];

const getInitialType = (source?: DataSourceModel | null) => source?.Type;

const getInitialSourceObjects = (source?: DataSourceModel | null): SourceObject[] => (
    source?.SourceConfig ? [toSourceObject(source.SourceConfig)] : []
);

const getInitialColumns = (source?: DataSourceModel | null): SourceColumn[] => columnsFromSourceConfig(source?.SourceConfig);

const getInitialDataTypes = (columns: SourceColumn[]): Record<string, DataTypeValue> => {
    const dataTypes: Record<string, DataTypeValue> = {};
    columns.forEach(column => {
        dataTypes[getColumnKey(column)] = normalizeDataType(column.DataType);
    });
    return dataTypes;
};

const DataSourceWizard: React.FC<DataSourceWizardProps> = ({
    facilityID,
    connectionTypes,
    source,
    saving,
    onSave,
    onCancel,
}) => {
    const initialType = getInitialType(source);
    const initialColumns = React.useMemo(() => getInitialColumns(source), [source]);
    const initialSourceObjects = React.useMemo(() => getInitialSourceObjects(source), [source]);

    const [activeStep, setActiveStep] = React.useState<WizardStep>(0);
    const [name, setName] = React.useState(source?.Name || '');
    const [type, setType] = React.useState<DataConnectionType | undefined>(initialType);
    const [connectionConfig, setConnectionConfig] = React.useState<Record<string, unknown>>(() => mergeConnectionConfigWithDefaults(initialType, source?.ConnectionConfig));
    const [originalConnectionConfig] = React.useState<Record<string, unknown>>(() => mergeConnectionConfigWithDefaults(initialType, source?.ConnectionConfig));
    const [testResult, setTestResult] = React.useState<ConnectionValidationResult | null>(null);
    const [connectionTestSuccessful, setConnectionTestSuccessful] = React.useState(Boolean(source?.ID));
    const [sourceObjects, setSourceObjects] = React.useState<SourceObject[]>(initialSourceObjects);
    const [selectedSourceKey, setSelectedSourceKey] = React.useState(source?.SourceConfig ? getSourceObjectKey(source.SourceConfig) : '');
    const [columns, setColumns] = React.useState<SourceColumn[]>(initialColumns);
    const [selectedColumnKeys, setSelectedColumnKeys] = React.useState<string[]>(initialColumns.map(getColumnKey));
    const [dataTypesByColumnKey, setDataTypesByColumnKey] = React.useState<Record<string, DataTypeValue>>(() => getInitialDataTypes(initialColumns));
    const [errors, setErrors] = React.useState<WizardErrors>({});
    const [dirty, setDirty] = React.useState(false);
    const [testing, setTesting] = React.useState(false);
    const [discoveringSources, setDiscoveringSources] = React.useState(false);
    const [discoveringColumns, setDiscoveringColumns] = React.useState(false);
    const [discoveredSourcesSignature, setDiscoveredSourcesSignature] = React.useState('');
    const [discoveredColumnsSignature, setDiscoveredColumnsSignature] = React.useState('');
    const isLinuxHost = connectionTypes.some(connectionType => connectionType.IsLinux === true);

    const selectedSource = React.useMemo(
        () => sourceObjects.find(item => getSourceObjectKey(item) === selectedSourceKey) || null,
        [selectedSourceKey, sourceObjects]
    );
    const canUseExistingEditSchema = Boolean(source?.ID) && !hasConnectionConfigChanged(originalConnectionConfig, connectionConfig);
    const testResultMessage = testResult
        ? testResult.Message || (testResult.Successful ? InovxLoader.t('facility:dataSources.connectionSuccessful') : InovxLoader.t('facility:dataSources.connectionFailed'))
        : '';

    const markDirty = React.useCallback(() => setDirty(true), []);

    const updateName = (value: string) => {
        setName(value);
        markDirty();
    };

    const updateType = (value: DataConnectionType) => {
        setType(value);
        setConnectionConfig(getDefaultConnectionConfig(value));
        setConnectionTestSuccessful(false);
        setTestResult(null);
        setSourceObjects([]);
        setSelectedSourceKey('');
        setColumns([]);
        setSelectedColumnKeys([]);
        setDataTypesByColumnKey({});
        setDiscoveredSourcesSignature('');
        setDiscoveredColumnsSignature('');
        markDirty();
    };

    const updateConnectionConfig = (key: string, value: unknown) => {
        setConnectionConfig(current => {
            const next = { ...current, [key]: value };
            if (key === 'authentication' && value === 'windows') {
                delete next.username;
                delete next.password;
            }
            return next;
        });
        setConnectionTestSuccessful(false);
        setTestResult(null);
        setSourceObjects([]);
        setSelectedSourceKey('');
        setColumns([]);
        setSelectedColumnKeys([]);
        setDataTypesByColumnKey({});
        setDiscoveredSourcesSignature('');
        setDiscoveredColumnsSignature('');
        markDirty();
    };

    const getRequiredMessage = () => InovxLoader.t('facility:dataSources.validation.required');

    const validateProperties = (): boolean => {
        const nextErrors: WizardErrors = {};
        if (!name.trim()) {
            nextErrors.name = getRequiredMessage();
        }
        if (!type) {
            nextErrors.type = getRequiredMessage();
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const validateConnectionData = (): boolean => {
        const nextErrors: WizardErrors = {};
        if (!validateProperties()) {
            return false;
        }

        if (!type) {
            return false;
        }

        if (isFileProvider(type)) {
            if (!String(connectionConfig.filepath || '').trim()) {
                nextErrors.filepath = getRequiredMessage();
            }
        } else {
            ['serverhost', 'database'].forEach(key => {
                if (!String(connectionConfig[key] || '').trim()) {
                    nextErrors[key] = getRequiredMessage();
                }
            });

            const portValue = String(connectionConfig.port ?? '').trim();
            const port = Number(portValue);
            if (portValue && (!Number.isFinite(port) || port <= 0)) {
                nextErrors.port = InovxLoader.t('facility:dataSources.validation.positivePort');
            }

            const sqlAuth = type !== DataConnectionType.MsSqlServer || connectionConfig.authentication !== 'windows';
            if (sqlAuth) {
                if (!String(connectionConfig.username || '').trim()) {
                    nextErrors.username = getRequiredMessage();
                }
                if (!String(connectionConfig.password || '').trim()) {
                    nextErrors.password = getRequiredMessage();
                }
            }
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const validateSchema = (): boolean => {
        const nextErrors: WizardErrors = {};
        if (!selectedSource) {
            nextErrors.source = getRequiredMessage();
        }
        if (selectedSource && !columnsLoadedFromCurrentSource) {
            nextErrors.columns = InovxLoader.t('facility:dataSources.loadingData');
        }
        if (selectedColumnKeys.length === 0) {
            nextErrors.columns = InovxLoader.t('facility:dataSources.validation.selectColumn');
        }
        const hasMissingDataType = selectedColumnKeys.some(columnKey => !dataTypesByColumnKey[columnKey]);
        if (hasMissingDataType) {
            nextErrors.columns = InovxLoader.t('facility:dataSources.validation.selectDataTypes');
        }
        const invalidDataType = selectedColumnKeys.some(columnKey => {
            const column = columns.find(item => getColumnKey(item) === columnKey);
            if (!column) {
                return true;
            }

            const sourceDataType = getSourceDataType(column);
            const selectedDataType = normalizeDataType(dataTypesByColumnKey[columnKey] || sourceDataType);
            return coerceDataTypeForProvider(type, sourceDataType, selectedDataType) !== selectedDataType;
        });
        if (invalidDataType) {
            nextErrors.columns = InovxLoader.t('facility:dataSources.validation.selectDataTypes');
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const testConnection = async () => {
        if (!validateConnectionData() || !type) {
            return;
        }

        setTesting(true);
        setTestResult(null);
        setErrors({});
        try {
            const result = await dataSourcesApi.testConnection({
                FacilityID: facilityID,
                Type: type,
                ConnectionConfig: connectionConfig,
                DataConnectionID: canUseExistingEditSchema ? source?.DataConnectionID : null,
            });
            setTestResult(result);
            setConnectionTestSuccessful(result.Successful === true);
            setSourceObjects([]);
            setSelectedSourceKey('');
            setColumns([]);
            setSelectedColumnKeys([]);
            setDataTypesByColumnKey({});
            setDiscoveredSourcesSignature('');
            setDiscoveredColumnsSignature('');
            if (!result.Successful) {
                setErrors({});
            } else {
                setErrors({});
            }
        } catch (error) {
            setConnectionTestSuccessful(false);
            displayApiError(error);
        } finally {
            setTesting(false);
        }
    };

    const getDiscoveryRequest = React.useCallback(() => ({
        FacilityID: facilityID,
        Type: type as DataConnectionType,
        ConnectionConfig: connectionConfig,
        DataConnectionID: canUseExistingEditSchema ? source?.DataConnectionID : null,
    }), [canUseExistingEditSchema, connectionConfig, facilityID, source?.DataConnectionID, type]);

    const getDiscoverySignature = React.useCallback(() => JSON.stringify({
        type,
        connectionConfig: canUseExistingEditSchema ? null : connectionConfig,
        dataConnectionID: canUseExistingEditSchema ? source?.DataConnectionID : null,
    }), [canUseExistingEditSchema, connectionConfig, source?.DataConnectionID, type]);

    const currentDiscoverySignature = getDiscoverySignature();
    const sourcesLoadedFromCurrentConnection = discoveredSourcesSignature === currentDiscoverySignature;
    const selectedSourceColumnsSignature = selectedSource && sourcesLoadedFromCurrentConnection
        ? `${currentDiscoverySignature}::${getSourceObjectKey(selectedSource)}`
        : '';
    const columnsLoadedFromCurrentSource = Boolean(selectedSourceColumnsSignature && discoveredColumnsSignature === selectedSourceColumnsSignature);
    const schemaLoading = discoveringSources || discoveringColumns || !sourcesLoadedFromCurrentConnection || (Boolean(selectedSource) && !columnsLoadedFromCurrentSource);

    const reconcileDiscoveredColumns = React.useCallback((discovered: SourceColumn[], discoveredSource: SourceObject) => {
        const dataTypes = getInitialDataTypes(discovered);
        const originalSourceKey = source?.SourceConfig ? getSourceObjectKey(source.SourceConfig) : '';
        const discoveredSourceKey = getSourceObjectKey(discoveredSource);

        if (source?.ID && originalSourceKey === discoveredSourceKey) {
            const savedColumns = columnsFromSourceConfig(source.SourceConfig);
            const discoveredKeySet = new Set(discovered.map(getColumnKey));
            const selectedKeys: string[] = [];

            savedColumns.forEach(savedColumn => {
                const key = getColumnKey(savedColumn);
                if (discoveredKeySet.has(key)) {
                    selectedKeys.push(key);
                    const discoveredColumn = discovered.find(column => getColumnKey(column) === key);
                    const sourceDataType = getSourceDataType(discoveredColumn || savedColumn);
                    const savedDataType = normalizeDataType(savedColumn.DataType);
                    dataTypes[key] = coerceDataTypeForProvider(type, sourceDataType, savedDataType);
                }
            });

            setSelectedColumnKeys(selectedKeys);
            setDataTypesByColumnKey(dataTypes);
            return;
        }

        setSelectedColumnKeys(source?.ID ? [] : discovered.map(getColumnKey));
        setDataTypesByColumnKey(dataTypes);
    }, [source, type]);

    const discoverSources = React.useCallback(async () => {
        if (!type || !validateConnectionData()) {
            return;
        }

        const signature = getDiscoverySignature();
        setDiscoveringSources(true);
        try {
            const discovered = await dataSourcesApi.discoverSources(getDiscoveryRequest());
            setSourceObjects(discovered);
            setDiscoveredSourcesSignature(signature);

            const currentSourceExists = selectedSourceKey && discovered.some(item => getSourceObjectKey(item) === selectedSourceKey);
            const originalSourceKey = source?.SourceConfig ? getSourceObjectKey(source.SourceConfig) : '';
            const originalSourceExists = originalSourceKey && discovered.some(item => getSourceObjectKey(item) === originalSourceKey);

            if (currentSourceExists) {
                setSelectedSourceKey(selectedSourceKey);
            } else if (originalSourceExists) {
                setSelectedSourceKey(originalSourceKey);
            } else if (discovered.length === 1) {
                setSelectedSourceKey(getSourceObjectKey(discovered[0]));
            } else {
                setSelectedSourceKey('');
            }
            setColumns([]);
            setSelectedColumnKeys([]);
            setDataTypesByColumnKey({});
            setDiscoveredColumnsSignature('');
        } catch (error) {
            setDiscoveredSourcesSignature('');
            displayApiError(error);
        } finally {
            setDiscoveringSources(false);
        }
    }, [getDiscoveryRequest, getDiscoverySignature, selectedSourceKey, source?.SourceConfig, type]);

    const discoverColumns = React.useCallback(async () => {
        if (!type || !validateConnectionData()) {
            return;
        }

        if (!selectedSource) {
            setErrors({ source: getRequiredMessage() });
            return;
        }

        const signature = `${getDiscoverySignature()}::${getSourceObjectKey(selectedSource)}`;
        setDiscoveringColumns(true);
        try {
            const discovered = await dataSourcesApi.discoverColumns({
                ...getDiscoveryRequest(),
                Source: selectedSource,
            });
            setColumns(discovered);
            reconcileDiscoveredColumns(discovered, selectedSource);
            setDiscoveredColumnsSignature(signature);
        } catch (error) {
            setDiscoveredColumnsSignature('');
            displayApiError(error);
        } finally {
            setDiscoveringColumns(false);
        }
    }, [getDiscoveryRequest, getDiscoverySignature, reconcileDiscoveredColumns, selectedSource, type]);

    const handleSourceChange = (sourceKey: string) => {
        setSelectedSourceKey(sourceKey);
        setColumns([]);
        setSelectedColumnKeys([]);
        setDataTypesByColumnKey({});
        setDiscoveredColumnsSignature('');
        markDirty();
    };

    const handleToggleColumn = (columnKey: string, selected: boolean) => {
        setSelectedColumnKeys(current => selected
            ? Array.from(new Set([...current, columnKey]))
            : current.filter(key => key !== columnKey));
        markDirty();
    };

    const handleDataTypeChange = (columnKey: string, dataType: DataTypeValue) => {
        setDataTypesByColumnKey(current => ({ ...current, [columnKey]: dataType }));
        markDirty();
    };

    React.useEffect(() => {
        if (activeStep !== 2 || !type || discoveringSources) {
            return;
        }

        const signature = currentDiscoverySignature;
        if (discoveredSourcesSignature === signature) {
            return;
        }

        discoverSources();
    }, [activeStep, currentDiscoverySignature, discoverSources, discoveredSourcesSignature, discoveringSources, type]);

    React.useEffect(() => {
        if (activeStep !== 2 || !type || !selectedSource || discoveringColumns || !sourcesLoadedFromCurrentConnection) {
            return;
        }

        const signature = `${currentDiscoverySignature}::${getSourceObjectKey(selectedSource)}`;
        if (discoveredColumnsSignature === signature) {
            return;
        }

        discoverColumns();
    }, [activeStep, currentDiscoverySignature, discoverColumns, discoveredColumnsSignature, discoveringColumns, selectedSource, sourcesLoadedFromCurrentConnection, type]);

    const goNext = () => {
        if (activeStep === 0 && validateProperties()) {
            setActiveStep(1);
        } else if (activeStep === 1) {
            if (!validateConnectionData()) {
                return;
            }
            if (!connectionTestSuccessful && !canUseExistingEditSchema) {
                setErrors({ connectionTest: InovxLoader.t('facility:dataSources.validation.testRequired') });
                return;
            }
            setErrors({});
            setActiveStep(2);
        }
    };

    const goBack = () => setActiveStep(step => Math.max(0, step - 1) as WizardStep);

    const canOpenStep = (targetStep: WizardStep): boolean => {
        if (targetStep <= activeStep) {
            return true;
        }

        if (!validateProperties()) {
            return false;
        }

        if (targetStep === 1) {
            return true;
        }

        if (!validateConnectionData()) {
            return false;
        }

        if (!connectionTestSuccessful && !canUseExistingEditSchema) {
            setErrors({ connectionTest: InovxLoader.t('facility:dataSources.validation.testRequired') });
            return false;
        }

        setErrors({});
        return true;
    };

    const openStep = (targetStep: WizardStep) => {
        if (targetStep === activeStep || !canOpenStep(targetStep)) {
            return;
        }

        setActiveStep(targetStep);
    };

    const save = async () => {
        if (!validateProperties() || !validateConnectionData() || !validateSchema() || !type || !selectedSource) {
            return;
        }

        await onSave({
            ID: source?.ID,
            VersionToken: source?.VersionToken,
            FacilityID: facilityID,
            DataConnectionID: source?.DataConnectionID || null,
            Name: name.trim(),
            Type: type,
            ConnectionConfig: { ...connectionConfig },
            SourceConfig: buildSourceConfig(selectedSource, columns, selectedColumnKeys, dataTypesByColumnKey),
            ConnectionTestSuccessful: testResult?.Successful === true,
            ConnectionTestMessage: testResult?.Successful === true ? testResult.Message || null : null,
        });
    };

    const closeWithoutSaving = () => onCancel();

    const requestClose = () => {
        if (dirty) {
            SystemActions.PublicActions.displaySaveChangesDialog({
                message: InovxLoader.t('common:msgBox.saveChanges'),
                confirmCallback: save,
                cancelCallback: closeWithoutSaving,
            });
            return;
        }

        closeWithoutSaving();
    };

    const beforeClose = (args: BeforeCloseEventArgs) => {
        if (dirty) {
            args.cancel = true;
            requestClose();
        }
    };

    const footerTemplate = () => (
        <div className="data-sources-wizard-footer">
            {activeStep === 1 && (
                <div className="data-sources-wizard-footer-test">
                    <button type="button" className="e-btn" disabled={testing || !type} onClick={testConnection}>
                        {testing ? InovxLoader.t('facility:dataSources.testingConnection') : InovxLoader.t('facility:dataSources.testConnection')}
                    </button>
                    {testResult && (
                        <span className={`data-sources-test-message ${testResult.Successful ? 'success' : 'failed'}`}>
                            {testResultMessage}
                        </span>
                    )}
                </div>
            )}
            <div className="data-sources-wizard-footer-actions">
                <button type="button" className="e-btn" onClick={requestClose} disabled={saving}>{InovxLoader.t('facility:form.cancel')}</button>
                {activeStep > 0 && <button type="button" className="e-btn" onClick={goBack} disabled={saving}>{InovxLoader.t('facility:dataSources.back')}</button>}
                {activeStep < 2 ? (
                    <button type="button" className="e-btn e-primary" onClick={goNext} disabled={saving}>{InovxLoader.t('facility:dataSources.next')}</button>
                ) : (
                    <button type="button" className="e-btn e-primary" onClick={save} disabled={saving || schemaLoading}>{saving ? InovxLoader.t('facility:form.saving') : InovxLoader.t('facility:dataSources.save')}</button>
                )}
            </div>
        </div>
    );

    const header = source?.ID
        ? InovxLoader.t('facility:dataSources.editTitle')
        : InovxLoader.t('facility:dataSources.addTitle');

    return (
        <DialogComponent
            target="#app"
            width="960px"
            header={header}
            visible={true}
            isModal={true}
            showCloseIcon={true}
            closeOnEscape={false}
            beforeClose={beforeClose}
            close={closeWithoutSaving}
            footerTemplate={footerTemplate}
            cssClass="data-sources-dialog"
            zIndex={110000}
        >
            <div className="data-sources-wizard">
                <div className="data-sources-wizard-tabs" role="tablist">
                    {wizardSteps.map(step => {
                        const isActive = activeStep === step.index;
                        const isReachable = step.index <= activeStep + 1;
                        return (
                            <button
                                key={step.index}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                className={`data-sources-wizard-tab${isActive ? ' active' : ''}`}
                                disabled={saving || !isReachable}
                                onClick={() => openStep(step.index)}
                            >
                                {InovxLoader.t(step.labelKey)}
                            </button>
                        );
                    })}
                </div>
                {activeStep === 0 && (
                    <DataSourcePropertiesStep
                        name={name}
                        type={type}
                        connectionTypes={connectionTypes}
                        errors={errors}
                        onNameChange={updateName}
                        onTypeChange={updateType}
                    />
                )}
                {activeStep === 1 && (
                    <ConnectionDataStep
                        type={type}
                        connectionTypes={connectionTypes}
                        config={connectionConfig}
                        errors={errors}
                        testResult={testResult}
                        onConfigChange={updateConnectionConfig}
                    />
                )}
                {activeStep === 2 && (
                    <DataSchemaStep
                        type={type}
                        isLinuxHost={isLinuxHost}
                        sourceObjects={sourceObjects}
                        selectedSourceKey={selectedSourceKey}
                        columns={columns}
                        selectedColumnKeys={selectedColumnKeys}
                        dataTypesByColumnKey={dataTypesByColumnKey}
                        errors={errors}
                        discoveringSources={discoveringSources}
                        discoveringColumns={discoveringColumns}
                        sourcesLoadedFromCurrentConnection={sourcesLoadedFromCurrentConnection}
                        columnsLoadedFromCurrentSource={columnsLoadedFromCurrentSource}
                        onSourceChange={handleSourceChange}
                        onToggleColumn={handleToggleColumn}
                        onDataTypeChange={handleDataTypeChange}
                    />
                )}
            </div>
        </DialogComponent>
    );
};

export default DataSourceWizard;
